import React, { useState, useEffect } from 'react';
import {
  DollarSign,
  Plus,
  Search,
  MoreHorizontal,
  Edit,
  Trash2,
  Star,
  RefreshCw,
  ArrowRightLeft,
  Globe
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Currencies = () => {
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [converterOpen, setConverterOpen] = useState(false);
  const [editingCurrency, setEditingCurrency] = useState(null);
  const [baseCurrency, setBaseCurrency] = useState(null);
  
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    symbol: '',
    exchange_rate: 1,
    is_base: false,
    is_active: true,
    decimal_places: 2
  });

  const [converterData, setConverterData] = useState({
    amount: 100,
    from_currency: 'USD',
    to_currency: 'EUR',
    result: null
  });

  useEffect(() => {
    fetchCurrencies();
    fetchBaseCurrency();
  }, []);

  const fetchCurrencies = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/currencies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrencies(res.data);
    } catch (err) {
      toast.error('Failed to fetch currencies');
    } finally {
      setLoading(false);
    }
  };

  const fetchBaseCurrency = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/currencies/base`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBaseCurrency(res.data);
    } catch (err) {
      console.error('Failed to fetch base currency');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      if (editingCurrency) {
        await axios.put(`${API_URL}/api/currencies/${editingCurrency.code}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Currency updated successfully');
      } else {
        await axios.post(`${API_URL}/api/currencies`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Currency added successfully');
      }
      setDialogOpen(false);
      fetchCurrencies();
      fetchBaseCurrency();
      resetForm();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save currency');
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      name: '',
      symbol: '',
      exchange_rate: 1,
      is_base: false,
      is_active: true,
      decimal_places: 2
    });
    setEditingCurrency(null);
  };

  const openEditDialog = (currency) => {
    setEditingCurrency(currency);
    setFormData({
      code: currency.code,
      name: currency.name,
      symbol: currency.symbol,
      exchange_rate: currency.exchange_rate,
      is_base: currency.is_base,
      is_active: currency.is_active,
      decimal_places: currency.decimal_places || 2
    });
    setDialogOpen(true);
  };

  const deleteCurrency = async (code) => {
    if (!window.confirm('Delete this currency?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/currencies/${code}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Currency deleted');
      fetchCurrencies();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete currency');
    }
  };

  const convertCurrency = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_URL}/api/currencies/convert`, {
        amount: converterData.amount,
        from_currency: converterData.from_currency,
        to_currency: converterData.to_currency
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConverterData({ ...converterData, result: res.data });
    } catch (err) {
      toast.error('Conversion failed');
    }
  };

  const filteredCurrencies = currencies.filter(c =>
    c.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Currencies</h1>
          <p className="text-muted-foreground">Manage currencies and exchange rates</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setConverterOpen(true)} className="gap-2">
            <ArrowRightLeft className="h-4 w-4" /> Converter
          </Button>
          <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="gap-2">
            <Plus className="h-4 w-4" /> Add Currency
          </Button>
        </div>
      </div>

      {/* Base Currency Info */}
      {baseCurrency && (
        <Card className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border-blue-500/20">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white text-2xl font-bold">
                  {baseCurrency.symbol}
                </div>
                <div>
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    {baseCurrency.name}
                    <Badge className="bg-amber-100 text-amber-700">
                      <Star className="h-3 w-3 mr-1 fill-amber-500" /> Base Currency
                    </Badge>
                  </h3>
                  <p className="text-muted-foreground">{baseCurrency.code} • All exchange rates are relative to this currency</p>
                </div>
              </div>
              <Globe className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search currencies..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Currencies Table */}
      <Card>
        <CardContent className="p-0">
          <table className="w-full">
            <thead className="border-b bg-muted/50">
              <tr>
                <th className="text-left p-4 font-medium">Currency</th>
                <th className="text-left p-4 font-medium">Code</th>
                <th className="text-left p-4 font-medium">Symbol</th>
                <th className="text-left p-4 font-medium">Exchange Rate</th>
                <th className="text-left p-4 font-medium">Status</th>
                <th className="text-right p-4 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCurrencies.map((currency) => (
                <tr key={currency.code} className="border-b hover:bg-muted/50">
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center font-bold text-lg">
                        {currency.symbol}
                      </div>
                      <div>
                        <div className="font-medium flex items-center gap-2">
                          {currency.name}
                          {currency.is_base && <Star className="h-4 w-4 text-amber-500 fill-amber-500" />}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="p-4 font-mono font-medium">{currency.code}</td>
                  <td className="p-4 text-xl">{currency.symbol}</td>
                  <td className="p-4">
                    {currency.is_base ? (
                      <span className="text-muted-foreground">1.0000 (base)</span>
                    ) : (
                      <span className="font-mono">{currency.exchange_rate?.toFixed(4)}</span>
                    )}
                  </td>
                  <td className="p-4">
                    {currency.is_active ? (
                      <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>
                    ) : (
                      <Badge className="bg-slate-100 text-slate-700">Inactive</Badge>
                    )}
                  </td>
                  <td className="p-4 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEditDialog(currency)}>
                          <Edit className="h-4 w-4 mr-2" /> Edit
                        </DropdownMenuItem>
                        {!currency.is_base && (
                          <DropdownMenuItem onClick={() => deleteCurrency(currency.code)} className="text-red-600">
                            <Trash2 className="h-4 w-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Add/Edit Currency Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCurrency ? 'Edit Currency' : 'Add New Currency'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Currency Code *</Label>
                <Input
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  placeholder="USD"
                  maxLength={3}
                  required
                  disabled={editingCurrency}
                />
              </div>
              <div className="space-y-2">
                <Label>Symbol *</Label>
                <Input
                  value={formData.symbol}
                  onChange={(e) => setFormData({ ...formData, symbol: e.target.value })}
                  placeholder="$"
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Currency Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="US Dollar"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Exchange Rate</Label>
                <Input
                  type="number"
                  step="0.0001"
                  value={formData.exchange_rate}
                  onChange={(e) => setFormData({ ...formData, exchange_rate: parseFloat(e.target.value) || 1 })}
                  disabled={formData.is_base}
                />
              </div>
              <div className="space-y-2">
                <Label>Decimal Places</Label>
                <Input
                  type="number"
                  min="0"
                  max="4"
                  value={formData.decimal_places}
                  onChange={(e) => setFormData({ ...formData, decimal_places: parseInt(e.target.value) || 2 })}
                />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
                <Label>Active</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_base}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_base: checked })}
                />
                <Label>Set as Base Currency</Label>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit">{editingCurrency ? 'Update' : 'Add'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Currency Converter Dialog */}
      <Dialog open={converterOpen} onOpenChange={setConverterOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Currency Converter</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Amount</Label>
              <Input
                type="number"
                value={converterData.amount}
                onChange={(e) => setConverterData({ ...converterData, amount: parseFloat(e.target.value) || 0, result: null })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>From</Label>
                <Select value={converterData.from_currency} onValueChange={(v) => setConverterData({ ...converterData, from_currency: v, result: null })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {currencies.filter(c => c.is_active).map((c) => (
                      <SelectItem key={c.code} value={c.code}>{c.code} - {c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>To</Label>
                <Select value={converterData.to_currency} onValueChange={(v) => setConverterData({ ...converterData, to_currency: v, result: null })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {currencies.filter(c => c.is_active).map((c) => (
                      <SelectItem key={c.code} value={c.code}>{c.code} - {c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <Button onClick={convertCurrency} className="w-full">
              <RefreshCw className="h-4 w-4 mr-2" /> Convert
            </Button>
            
            {converterData.result && (
              <div className="p-4 bg-muted rounded-lg text-center">
                <p className="text-sm text-muted-foreground mb-1">
                  {converterData.result.original_amount} {converterData.result.original_currency} =
                </p>
                <p className="text-3xl font-bold">
                  {converterData.result.converted_amount?.toLocaleString()} {converterData.result.target_currency}
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Rate: 1 {converterData.result.original_currency} = {converterData.result.exchange_rate?.toFixed(4)} {converterData.result.target_currency}
                </p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Currencies;
